import React, { useState, useEffect } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  updateDoc,
  doc,
} from "firebase/firestore";
import { db } from "../services/firebase";
import { useSeller } from "../contexts/SellerContext";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import "./BuyerRequest.css";
import PageHeader from "../components/PageHeader";

const BuyerRequest = () => {
  const { seller } = useSeller();
  const sellerId = seller?.sellerId;
  const location = useLocation();

  const serviceType = location.state?.serviceType;
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Accept drawer state
  const [acceptDrawer, setAcceptDrawer] = useState(null); // { order, variants, inputs: { [size]: { avgNetWt, avgGrossWt } } }

  // Extract variants safely from an order
  const getVariants = (order) => {
    if (
      Array.isArray(order.selectedVariants) &&
      order.selectedVariants.length > 0
    ) {
      return order.selectedVariants.map((v) => ({
        size: v.size || "Standard",
        quantity: v.quantity ?? v.set ?? v.qty ?? 1,
      }));
    }
    if (Array.isArray(order.lotDetails) && order.lotDetails.length > 0) {
      return order.lotDetails.map((l) => ({
        size: l.size || "Standard",
        quantity: l.set ?? l.quantity ?? l.qty ?? 1,
        avgNetWt: l.avgNetWt,
        avgGrossWt: l.avgGrossWt,
      }));
    }
    if (Array.isArray(order.variants) && order.variants.length > 0) {
      return order.variants.map((v) => ({
        size: v.size || "Standard",
        quantity: v.quantity ?? v.set ?? v.qty ?? 1,
        avgNetWt: v.avgNetWt,
        avgGrossWt: v.avgGrossWt,
      }));
    }
    if (order.size) {
      return [
        {
          size: order.size,
          quantity: order.quantity ?? order.set ?? order.qty ?? 1,
        },
      ];
    }
    return [
      {
        size: "Standard",
        quantity: order.quantity ?? order.set ?? order.qty ?? 1,
      },
    ];
  };

  // Fetch orders
  useEffect(() => {
    const fetchOrders = async () => {
      if (!sellerId) return;
      try {
        setLoading(true);
        const q = query(
          collection(db, "orderList"),
          where("sellerId", "==", sellerId),
          where("orderStatus", "==", "Requested"),
          where("serviceType", "==", serviceType),
        );
        const snapshot = await getDocs(q);
        const fetchedOrders = [];
        snapshot.forEach((docSnap) => {
          fetchedOrders.push({ firestoreId: docSnap.id, ...docSnap.data() });
        });
        setOrders(fetchedOrders);
      } catch (err) {
        console.error("Error fetching orders:", err);
        setError("Failed to load requests.");
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [sellerId, refreshTrigger, serviceType]);

  // Open the accept drawer — pre-fill existing lotDetails values if present
  const openAcceptDrawer = (order) => {
    const variants = getVariants(order);
    const inputs = {};
    variants.forEach((variant) => {
      const existing = (order.lotDetails || []).find(
        (l) => l.size === variant.size,
      );
      inputs[variant.size] = {
        avgNetWt:
          existing?.avgNetWt || variant.avgNetWt || order.avgNetWt || "",
        avgGrossWt:
          existing?.avgGrossWt || variant.avgGrossWt || order.avgGrossWt || "",
      };
    });
    setAcceptDrawer({ order, variants, inputs });
  };

  const closeAcceptDrawer = () => setAcceptDrawer(null);

  const handleWeightInput = (size, field, value) => {
    setAcceptDrawer((prev) => ({
      ...prev,
      inputs: {
        ...prev.inputs,
        [size]: { ...prev.inputs[size], [field]: value },
      },
    }));
  };

  // Confirm accept: update lotDetails in product, then accept order
  const handleConfirmAccept = async () => {
    if (!acceptDrawer) return;
    const { order, variants, inputs } = acceptDrawer;
    const { firestoreId } = order;

    // Validate all sizes have both fields filled
    for (const variant of variants) {
      const inp = inputs[variant.size];
      if (!inp?.avgNetWt || !inp?.avgGrossWt) {
        alert(
          `❌ Please fill Avg Net Wt and Avg Gross Wt for size ${variant.size}`,
        );
        return;
      }
      const netWt = parseFloat(inp.avgNetWt) || 0;
      const grossWt = parseFloat(inp.avgGrossWt) || 0;
      if (grossWt < netWt) {
        alert(
          `❌ Avg Gross Wt must be greater than or equal to Avg Net Wt for size ${variant.size}`,
        );
        return;
      }
    }

    setPendingAction("accept");
    try {
      const orderRef = doc(db, "orderList", firestoreId);
      const orderSnap = await getDoc(orderRef);
      if (!orderSnap.exists()) {
        alert("❌ Order not found.");
        setOrders((prev) => prev.filter((o) => o.firestoreId !== firestoreId));
        closeAcceptDrawer();
        return;
      }
      const data = orderSnap.data();
      if (data.sellerId !== sellerId) {
        alert("❌ Not authorized.");
        closeAcceptDrawer();
        return;
      }

      // Update product lotDetails with entered avgNetWt + avgGrossWt
      const prodId = order.id || order.productId;
      let updatedLots = [];
      if (prodId) {
        const productRef = doc(db, "products", prodId);
        const productSnap = await getDoc(productRef);
        if (productSnap.exists()) {
          const productData = productSnap.data();
          const existingLots = productData.lotDetails || [];

          // Merge: update matching sizes, add new sizes
          updatedLots = [...existingLots];
          variants.forEach((variant) => {
            const inp = inputs[variant.size];
            const avgNetWt = parseFloat(inp.avgNetWt) || 0;
            const avgGrossWt = parseFloat(inp.avgGrossWt) || 0;
            const idx = updatedLots.findIndex((l) => l.size === variant.size);
            if (idx !== -1) {
              updatedLots[idx] = {
                ...updatedLots[idx],
                avgNetWt: avgNetWt.toFixed(3),
                avgGrossWt: avgGrossWt.toFixed(3),
              };
            } else {
              updatedLots.push({
                size: variant.size,
                set: String(variant.quantity || 0),
                avgNetWt: avgNetWt.toFixed(3),
                avgGrossWt: avgGrossWt.toFixed(3),
                netWt: "0",
                grossWt: "0",
                avgSpecWt: "0.000",
              });
            }
          });

          await updateDoc(productRef, { lotDetails: updatedLots });
        }
      }

      // Accept order
      const nextStatus = serviceType === "order" ? "Preparation" : "Assortment";
      const orderUpdatePayload = {
        orderStatus: nextStatus,
        updatedAt: new Date(),
      };
      if (updatedLots.length > 0) {
        orderUpdatePayload.lotDetails = updatedLots;
      }
      await updateDoc(orderRef, orderUpdatePayload);

      setRefreshTrigger((prev) => prev + 1);
      closeAcceptDrawer();
      alert(`✅ Order accepted and moved to ${nextStatus}!`);
    } catch (err) {
      console.error("Error accepting order:", err);
      alert(`❌ Failed to accept order: ${err.message}`);
    } finally {
      setPendingAction(null);
    }
  };

  // Accept order directly without drawer for "ready" service type
  const handleDirectAccept = async (firestoreId) => {
    if (pendingAction) return;
    setPendingAction("accept");
    try {
      const orderRef = doc(db, "orderList", firestoreId);
      const orderSnap = await getDoc(orderRef);
      if (!orderSnap.exists()) {
        alert("❌ Order not found.");
        setOrders((prev) => prev.filter((o) => o.firestoreId !== firestoreId));
        return;
      }
      const data = orderSnap.data();
      if (data.sellerId !== sellerId) {
        alert("❌ Not authorized.");
        return;
      }

      const nextStatus = serviceType === "order" ? "Preparation" : "Assortment";
      await updateDoc(orderRef, {
        orderStatus: nextStatus,
        updatedAt: new Date(),
      });

      setRefreshTrigger((prev) => prev + 1);
      alert(`✅ Order accepted and moved to ${nextStatus}!`);
    } catch (err) {
      console.error("Error accepting order:", err);
      alert(`❌ Failed to accept order: ${err.message}`);
    } finally {
      setPendingAction(null);
    }
  };

  // Handle Reject
  const handleReject = async (firestoreId) => {
    if (pendingAction) return;
    if (!window.confirm("Are you sure you want to reject this request?"))
      return;
    setPendingAction("reject");
    try {
      const orderRef = doc(db, "orderList", firestoreId);
      const orderSnap = await getDoc(orderRef);
      if (!orderSnap.exists()) {
        alert("❌ Order not found.");
        setOrders((prev) => prev.filter((o) => o.firestoreId !== firestoreId));
        return;
      }
      const data = orderSnap.data();
      if (data.sellerId !== sellerId) {
        alert("❌ Unauthorized reject attempt.");
        return;
      }

      const num = (v) => parseFloat(v) || 0;
      const prodId = data.id || data.productId;
      if (
        data.Lot &&
        Array.isArray(data.lotDetails) &&
        data.selectedVariants?.length > 0 &&
        prodId
      ) {
        const productRef = doc(db, "products", prodId);
        const productSnap = await getDoc(productRef);
        if (productSnap.exists()) {
          const productData = productSnap.data();
          const updatedLotDetails = (productData.lotDetails || []).map(
            (lot) => {
              const variant = data.selectedVariants.find(
                (v) => v.size === lot.size,
              );
              if (!variant) return lot;
              return { ...lot, set: num(lot.set) + num(variant.quantity) };
            },
          );
          await updateDoc(productRef, { lotDetails: updatedLotDetails });
        }
      }

      await updateDoc(orderRef, {
        orderStatus: "Rejected",
        updatedAt: new Date(),
      });
      setRefreshTrigger((prev) => prev + 1);
      alert("🚫 Request rejected.");
    } catch (err) {
      console.error("Error rejecting order:", err);
      alert(`❌ Failed to reject: ${err.message}`);
    } finally {
      setPendingAction(null);
    }
  };
  const isCaratSegment = (order) => {
    const segment = String(order.segment || "")
      .trim()
      .toUpperCase();
    return segment === "PEARL" || segment === "GEMS";
  };
  const getNeteWt = (order, size) => {
    const num = (v) => parseFloat(v) || 0;
    const lotDetail = order.lotDetails?.find((lot) => lot.size === size);
    if (!lotDetail) return "0.000";
    return num(lotDetail.avgNetWt).toFixed(3);
  };

  if (loading) {
    return (
      <>
        <PageHeader title="Buyer Request" />
        <div className="workflow-container">Loading...</div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageHeader title="Buyer Request" />
        <div className="workflow-container">
          <p className="error">{error}</p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Buyer Request" />
      <p className="results-count">
        {orders.length} request{orders.length !== 1 ? "s" : ""} found
      </p>

      <div className="orders-grid">
        {orders.length === 0 ? (
          <p className="no-results">No matching requests.</p>
        ) : (
          orders.map((order) => {
            const imageUrl = order.images?.[0]?.url?.trim();
            const orderVariants = getVariants(order);
            return (
              <div key={order.firestoreId} className="order-card">
                <div className="order-image">
                  {imageUrl ? (
                    <img src={imageUrl} alt={order.productName} />
                  ) : (
                    <div className="image-placeholder">No Image</div>
                  )}
                </div>
                <div className="order-info">
                  <span className="service-badge">
                    {order.serviceType?.toUpperCase()}
                  </span>
                  <h5>
                    {order.category}/{order.productSource}/{order.productName}
                  </h5>
                  <p>
                    {order.specification}/{order.styleType}
                  </p>

                  <span className="br-design-code">
                    Design:{order.designCode}
                  </span>

                  {isCaratSegment ? (
                    <>
                      <p className="spec-label">
                        {orderVariants.map((variant, index) => (
                          <span key={variant.size}>
                            {variant.size} x {variant.quantity} ~
                            {getNeteWt(order, variant.size)}g
                            {index < orderVariants.length - 1 ? ", " : ""}
                          </span>
                        ))}
                      </p>
                    </>
                  ) : (
                    orderVariants.length > 0 && (
                      <p className="spec-label">
                        {orderVariants.map((variant, index) => (
                          <span key={variant.size}>
                            {variant.size} x {variant.quantity} ~
                            {getNeteWt(order, variant.size)}g
                            {index < orderVariants.length - 1 ? ", " : ""}
                          </span>
                        ))}
                      </p>
                    )
                  )}
                  <div className="action-btn">
                    <button
                      className="reject-btn"
                      disabled={!!pendingAction}
                      onClick={() => handleReject(order.firestoreId)}
                      type="button"
                    >
                      Reject
                    </button>
                    <button
                      className="accept-btn"
                      disabled={!!pendingAction}
                      onClick={() =>
                        serviceType === "ready"
                          ? handleDirectAccept(order.firestoreId)
                          : openAcceptDrawer(order)
                      }
                      type="button"
                    >
                      Accept
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Accept Drawer */}
      {acceptDrawer && (
        <div className="br-drawer-overlay" onClick={closeAcceptDrawer}>
          <div className="br-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="br-drawer-header">
              <div className="br-drawer-header-text">
                <h3>Fill Weight Details</h3>
                <p className="br-drawer-sub">
                  {acceptDrawer.order.category} /{" "}
                  {acceptDrawer.order.productName}
                </p>
              </div>
              <button
                className="br-drawer-close"
                onClick={closeAcceptDrawer}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="br-drawer-body">
              {acceptDrawer.variants.length === 0 ? (
                <p className="no-variants-msg">
                  No size variants found for this order.
                </p>
              ) : (
                acceptDrawer.variants.map((variant) => {
                  const rawNet = acceptDrawer.inputs[variant.size]?.avgNetWt;
                  const rawGross =
                    acceptDrawer.inputs[variant.size]?.avgGrossWt;
                  const netWt = parseFloat(rawNet) || 0;
                  const grossWt = parseFloat(rawGross) || 0;
                  const isError =
                    rawNet !== undefined &&
                    rawNet !== "" &&
                    rawGross !== undefined &&
                    rawGross !== "" &&
                    grossWt < netWt;
                  return (
                    <div key={variant.size} className="br-size-block">
                      <div className="br-size-header">
                        <span className="br-design-code">
                          {acceptDrawer.order.designCode}
                        </span>
                        <span className="br-size-chip">{variant.size}</span>
                        <span className="br-qty-label">
                          Qty: {variant.quantity}
                        </span>
                      </div>
                      <div className="br-inputs-row">
                        <label className="br-input-group">
                          <span>Avg Gross Wt (g)</span>
                          <input
                            type="number"
                            min="0"
                            step="0.001"
                            placeholder="e.g. 10.000"
                            value={
                              acceptDrawer.inputs[variant.size]?.avgGrossWt ||
                              ""
                            }
                            onChange={(e) =>
                              handleWeightInput(
                                variant.size,
                                "avgGrossWt",
                                e.target.value,
                              )
                            }
                            className={isError ? "br-error" : ""}
                          />
                        </label>
                        <label className="br-input-group">
                          <span>Avg Net Wt (g)</span>
                          <input
                            type="number"
                            min="0"
                            step="0.001"
                            placeholder="e.g. 8.000"
                            value={
                              acceptDrawer.inputs[variant.size]?.avgNetWt || ""
                            }
                            onChange={(e) =>
                              handleWeightInput(
                                variant.size,
                                "avgNetWt",
                                e.target.value,
                              )
                            }
                            className={isError ? "br-error" : ""}
                          />
                        </label>
                      </div>
                      {isError && (
                        <p className="br-field-error">
                          Gross Wt must be greater than or equal to Net Wt
                        </p>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {(() => {
              const hasWeightError = acceptDrawer.variants.some((variant) => {
                const inp = acceptDrawer.inputs[variant.size];
                if (!inp?.avgNetWt || !inp?.avgGrossWt) return false;
                const netWt = parseFloat(inp.avgNetWt) || 0;
                const grossWt = parseFloat(inp.avgGrossWt) || 0;
                return grossWt < netWt;
              });
              const hasEmptyInput = acceptDrawer.variants.some((variant) => {
                const inp = acceptDrawer.inputs[variant.size];
                return !inp?.avgNetWt || !inp?.avgGrossWt;
              });
              return (
                <div className="br-drawer-footer">
                  <button
                    className="br-cancel-btn"
                    onClick={closeAcceptDrawer}
                    disabled={!!pendingAction}
                  >
                    Cancel
                  </button>
                  <button
                    className="br-confirm-btn"
                    onClick={handleConfirmAccept}
                    disabled={
                      !!pendingAction || hasWeightError || hasEmptyInput
                    }
                  >
                    {pendingAction ? "Saving..." : "Confirm & Accept"}
                  </button>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </>
  );
};

export default BuyerRequest;
